import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, message, Alert, Collapse, Form, Input, Space, Spin, Switch, Descriptions, Tag, Modal } from 'antd';
import { LeftOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import '../CSS/Buttons.css';
import dayjs from 'dayjs';
import Cookies from 'js-cookie'
import { Typography, ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Box } from '@mui/system';
import NavigationBar from './NavigationBar';
import { useNavigate } from 'react-router-dom';
import BullhornIntegrationPanel from './BullhornIntegrationPanel';
import { loadStripe } from '@stripe/stripe-js';

const backend_url = process.env.REACT_APP_BACKEND_URL
const { Panel } = Collapse;

// Material 3 Expressive Theme (matching other components)
const m3ExpressiveTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#FA370A',
            light: '#FF6B47',
            dark: '#C42D08',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#FF45C1',
            light: '#FF7AD4',
            dark: '#E63AAD',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#0A0A0F',
            paper: 'rgba(255, 255, 255, 0.05)',
        },
        text: {
            primary: '#FFFFFF',
            secondary: 'rgba(255, 255, 255, 0.7)',
        },
    },
    typography: {
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
    },
});

const SettingsPage = ({onLogout, loggedIn, getCookie}) => {
    const [existingJDList, setExistingJDList] = useState([]);
    const [hideClosedJobs, setHideClosedJobs] = useState(false); // New state for hiding closed jobs
    const [jobLimits, setJobLimits] = useState({
        CVUsage: 0,
        CVLimit: 0,
        NumOpenJob: 0,
        JobLimit: 0,
        PrescreenUsage: 0,
        PrescreenLimit: 0,
        plan_type: '',
        next_payment_date: '',
        billing_status: ''
    });
    const [jobAdderIntegrated, setJobAdderIntegrated] = useState(false);
    const [jobAdderURL, setjobAdderURL] = useState(''); // Basic Integration Setup URL
    const [reauthJobAdderURL, setReauthJobAdderURL] = useState(''); // This is generated when reauth button is pressed - slightly different to the generic JobAdderSetupURL above
    const [jobAdderAuthCode, setJobAdderAuthCode] = useState(null);
    const [showDeleteText, setShowDeleteText] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false); // Loading status of delete integration button
    const [integrateLoading, setIntegrateLoading] = useState(false); // Loading status of setup integration button
    const [returnedReauth, setReturnedReauth] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [processingBullhornRedirect, setProcessingBullhornRedirect] = useState(false);
    
    // Subscription management states
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [subscriptionAction, setSubscriptionAction] = useState(''); // 'upgrade', 'downgrade', 'cancel', 'update_payment'

    // Payment form states for updating payment method
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    
    // Cancellation fee states
    const [showCancellationFeeModal, setShowCancellationFeeModal] = useState(false);
    const [cancellationFeeData, setCancellationFeeData] = useState(null);
    const [showCancellationPaymentForm, setShowCancellationPaymentForm] = useState(false);
    const [cancellationPaymentData, setCancellationPaymentData] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    const [checkout, setCheckout] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState('');

    // Reactivation states
    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [reactivatePlans, setReactivatePlans] = useState([]);
    const [selectedReactivatePlan, setSelectedReactivatePlan] = useState(null);

    // Trial upgrade states
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradePlans, setUpgradePlans] = useState({ monthly: [], oneoff: [] });
    const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
    const [isUpgradeModalForTrial, setIsUpgradeModalForTrial] = useState(false);
    const [upgradeActiveBillingTab, setUpgradeActiveBillingTab] = useState('monthly');
    const [upgradeSelectedDuration, setUpgradeSelectedDuration] = useState({
        basic: '1year',
        standard: '1year',
        premium: '1year'
    });
    const [isPrimaryUser, setIsPrimaryUser] = useState(false);
    const [orgUsers, setOrgUsers] = useState([]);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [newUserForm, setNewUserForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', isPrimaryUser: false });

    const navigate = useNavigate();

    const handleLogoutAndRedirect = () => {
        onLogout();
    };

    // Subscription management handlers
    const handleSubscriptionAction = (action) => {
        Modal.destroyAll();
        setSubscriptionAction(action);
        setShowAddUserModal(false);
        setShowPaymentForm(false);
        setShowCancellationFeeModal(false);
        setShowCancellationPaymentForm(false);
        setShowSubscriptionModal(true);
    };

    const handleSubscriptionModalClose = () => {
        setShowSubscriptionModal(false);
        setSubscriptionAction('');
    };

    // Initialize Stripe by fetching configuration from backend
    useEffect(() => {
        const initializeStripe = async () => {
            try {
                const response = await fetch(`${backend_url}/v1/stripe-config`);
                const data = await response.json();
                if (data.success && data.config.publishable_key) {
                    setStripePromise(loadStripe(data.config.publishable_key));
                } else {
                    console.error('Failed to load Stripe configuration');
                }
            } catch (error) {
                console.error('Error loading Stripe configuration:', error);
            }
        };
        
        initializeStripe();
    }, []);

    useEffect(() => {
        if (subscriptionAction === 'cancel') {
            handleCancelSubscription();
        }
        else if (subscriptionAction === 'update_payment') {
            handleUpdatePaymentMethod();
        }
        else if (subscriptionAction === 'upgrade') {
            handleUpgradeAction();
        }
    }, [subscriptionAction]);

    useEffect(() => {
        const initializePaymentElement = async () => {
            if (showPaymentForm && paymentData && !checkout) {
                try {
                    const stripe = await stripePromise;
                    const elements = stripe.elements({
                        clientSecret: paymentData.clientSecret,
                    });
                    
                    const paymentElement = elements.create('payment');
                    paymentElement.mount('#payment-element-update');
                    
                    setCheckout({ stripe, elements });
                } catch (elementError) {
                    console.error('Error initializing Payment Element:', elementError);
                    setPaymentStatus('Failed to initialize payment form. Please try again.');
                }
            }
        };

        initializePaymentElement();
    }, [showPaymentForm, paymentData, checkout]);

    // Initialize cancellation fee payment element
    useEffect(() => {
        const initializeCancellationPaymentElement = async () => {
            if (showCancellationPaymentForm && cancellationPaymentData && stripePromise) {
                try {
                    const stripe = await stripePromise;
                    const elements = stripe.elements({
                        clientSecret: cancellationPaymentData.clientSecret,
                    });
                    
                    const paymentElement = elements.create('payment');
                    paymentElement.mount('#cancellation-payment-element');
                    
                    // Handle payment submission
                    const handleSubmit = async (event) => {
                        event.preventDefault();
                        
                        const { error } = await stripe.confirmPayment({
                            elements,
                            confirmParams: {
                                return_url: window.location.origin + '/settings',
                            },
                        });
                        
                        if (error) {
                            message.error(error.message);
                        } else {
                            message.success('Payment successful! Subscription will be cancelled.');
                            setShowCancellationPaymentForm(false);
                            fetchSettings();
                        }
                    };
                    
                    // Store the submit handler for later use
                    window.cancellationPaymentSubmit = handleSubmit;
                } catch (elementError) {
                    console.error('Error initializing Cancellation Payment Element:', elementError);
                    message.error('Failed to initialize payment form. Please try again.');
                }
            }
        };

        initializeCancellationPaymentElement();
    }, [showCancellationPaymentForm, cancellationPaymentData, stripePromise]);

    const fetchOrgUsers = async () => {
        try {
            const res = await axios.get(`${backend_url}/v1/organization-users?sessionID=${getCookie("userSession")}`);
            if (res.data.success) {
                setOrgUsers(res.data.users);
                setIsPrimaryUser(true);
                console.log('Primary user verified, loaded', res.data.totalUsers, 'users');
            }
        } catch (err) {
            console.log('Fetch org users error:', err.response?.status, err.response?.data);
            if (err.response?.status === 403) {
                setIsPrimaryUser(false);
            } else {
                setIsPrimaryUser(false);
            }
        }
    };

    const handleAddUser = async () => {
        try {
            await axios.post(`${backend_url}/v1/organization-users/add?sessionID=${getCookie("userSession")}`, newUserForm);
            message.success('User added successfully');
            setShowAddUserModal(false);
            setNewUserForm({ firstName: '', lastName: '', email: '', phone: '', password: '', isPrimaryUser: false });
            fetchOrgUsers();
        } catch (err) {
            if (err.response?.status === 409) {
                message.error('Email already exists');
            } else {
                message.error('Failed to add user');
            }
        }
    };

    const handleRemoveUser = async (customerID, userName) => {
        Modal.confirm({
            title: 'Remove User',
            content: `Are you sure you want to remove ${userName}?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: async () => {
                try {
                    await axios.post(`${backend_url}/v1/organization-users/remove?sessionID=${getCookie("userSession")}`, { customerID });
                    message.success('User removed successfully');
                    fetchOrgUsers();
                } catch (err) {
                    message.error('Failed to remove user');
                }
            }
        });
    };

              const handleCancelSubscription = async () => {                                            
        try {
            const response = await axios.post(
                `${backend_url}/v1/cancel-subscription`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Cookie-Value': getCookie("userSession")
                    }
                }
            );
            
            if (response.data.success) {
                if (response.data.cancel_at_period_end) {
                    message.info('Subscription is already scheduled for cancellation');
                } else {
                    message.success('Subscription cancellation scheduled successfully');
                }
                fetchSettings(); 
            } else if (response.data.requires_payment) {
                // Show cancellation fee confirmation modal
                setCancellationFeeData({
                    amount: response.data.cancellation_fee,
                    currency: response.data.currency,
                    stripeCustomerId: response.data.stripe_customer_id
                });
                setShowCancellationFeeModal(true);
            } else {
                message.error(response.data.error || 'Failed to cancel subscription');
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            message.error('Failed to cancel subscription,please contact the primary user');
        } finally {
            setSubscriptionAction('');
            setShowSubscriptionModal(false);
        }
    };

    const handleCancellationFeeConfirm = async () => {
        setShowCancellationFeeModal(false);
        setShowCancellationPaymentForm(true);
        
        // Automatically create payment intent when showing payment form
        await handleCancellationFeePayment();
    };

    const handleCancellationFeePayment = async () => {
        try {
            const response = await axios.post(
                `${backend_url}/v1/create-payment-intent`,
                {
                    payment_type: 'cancellation_fee',
                    customer_id: cancellationFeeData.stripeCustomerId,
                    email: 'user@example.com', 
                    name: 'User Name' 
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Cookie-Value': getCookie("userSession")
                    }
                }
            );

            if (response.data.success) {
                setCancellationPaymentData({
                    clientSecret: response.data.client_secret,
                    amount: response.data.amount,
                    currency: response.data.currency
                });
                // Payment element will be initialized by useEffect
            } else {
                message.error(response.data.error || 'Failed to create payment intent');
            }
        } catch (error) {
            console.error('Error creating cancellation fee payment:', error);
            message.error('Failed to create payment intent');
        }
    };

    const handleUpdatePaymentMethod = async () => {
        try {
            const response = await axios.post(
                `${backend_url}/v1/update-payment-method`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Cookie-Value': getCookie("userSession")
                    }
                }
            );
            
            if (response.data.success) {
                setPaymentData({
                    clientSecret: response.data.client_secret,
                    setupIntentId: response.data.setup_intent_id,
                    customerId: response.data.customer_id
                });
                setShowPaymentForm(true);
            } else {
                message.error(response.data.error || 'Failed to initiate payment method update');
            }
        } catch (error) {
            console.error('Error updating payment method:', error);
            // Check if error is due to non-primary user attempting to update payment method
            if (error.response && error.response.status === 403) {
                message.error('Failed to update payment method, please contact the primary user');
            } else {
                message.error('Failed to update payment method,please contact the primary user');
            }
        } finally {
            setSubscriptionAction('');
            setShowSubscriptionModal(false);
        }
    };

    // Fetch available plans for reactivation
    const fetchReactivationPlans = async () => {
        try {
            const response = await axios.get(`${backend_url}/v1/subscription-plans`);
            if (response.data.success) {
                const allPlans = [
                    ...(response.data.monthly || []),
                    ...(response.data.oneoff || [])
                ];
                setReactivatePlans(allPlans);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            message.error('Failed to load subscription plans');
        }
    };

    // Handle reactivation
    const handleReactivateSubscription = async () => {
        if (!selectedReactivatePlan) {
            message.error('Please select a plan');
            return;
        }

        try {
            const response = await axios.post(
                `${backend_url}/v1/reactivate-subscription`,
                { plan_id: selectedReactivatePlan.id },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Cookie-Value': getCookie("userSession")
                    }
                }
            );

            if (response.data.success) {
                message.success('Subscription created! Your service will activate after payment is confirmed.');
                setShowReactivateModal(false);
                setSelectedReactivatePlan(null);
                // Refresh page data after a short delay to let webhook process
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                message.error(response.data.error || 'Failed to reactivate service');
            }
        } catch (error) {
            console.error('Error reactivating subscription:', error);
            // Check if error is due to non-primary user attempting to reactivate
            if (error.response && error.response.status === 403) {
                message.error('Failed to reactivate, please contact the primary user');
            } else {
                message.error(error.response?.data?.error || 'Failed to reactivate service');
            }
        }
    };

    // Handle upgrade action
    const handleUpgradeAction = async () => {
        setShowSubscriptionModal(false);
        
        // Check if user is in trial
        const isTrial = jobLimits.billing_status === 'trial' || jobLimits.plan_type === 'freetrail_7day';
        
        if (isTrial) {
            // Trial user: show upgrade modal
            await fetchUpgradePlans();
            setIsUpgradeModalForTrial(true);
            setShowUpgradeModal(true);
        } else {
            // Non-trial user: show "feature under development" modal
            setIsUpgradeModalForTrial(false);
            setShowUpgradeModal(true);
        }
    };

    // Fetch available plans for upgrade
    const fetchUpgradePlans = async () => {
        try {
            const response = await axios.get(`${backend_url}/v1/subscription-plans`);
            if (response.data.success) {
                // Store plans separately for monthly and oneoff
                const monthlyPlans = (response.data.monthly || []).filter(plan => plan.id !== 'freetrail_7day');
                const oneoffPlans = (response.data.oneoff || []).filter(plan => plan.id !== 'freetrail_7day');
                
                setUpgradePlans({
                    monthly: monthlyPlans,
                    oneoff: oneoffPlans
                });
                console.log('Loaded upgrade plans:', { monthly: monthlyPlans, oneoff: oneoffPlans });
            }
        } catch (error) {
            console.error('Error fetching upgrade plans:', error);
            message.error('Failed to load subscription plans');
        }
    };

    // Handle trial upgrade
    const handleUpgradeFromTrial = async () => {
        if (!selectedUpgradePlan) {
            message.error('Please select a plan');
            return;
        }

        try {
            const response = await axios.post(
                `${backend_url}/v1/upgrade-from-trial`,
                { plan_id: selectedUpgradePlan.id },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Cookie-Value': getCookie("userSession")
                    }
                }
            );

            if (response.data.success) {
                // Check if payment method collection is needed
                if (response.data.step === 'collect_payment_method') {
                    // Show payment form to collect payment method
                    setPaymentData({
                        clientSecret: response.data.client_secret,
                        setup_intent_id: response.data.setup_intent_id,
                        customer_id: response.data.customer_id,
                        plan_id: response.data.plan_id,
                        plan_name: response.data.plan_name,
                        amount: response.data.amount,
                        currency: response.data.currency,
                        step: 'collect_payment_method',
                        isTrialUpgrade: true
                    });
                    setShowPaymentForm(true);
                    setShowUpgradeModal(false);
                    message.info('Please add a payment method to continue with the upgrade');
                } else {
                    // Payment method exists, upgrade initiated
                    message.success(`Upgrading to ${response.data.plan_name}! Payment will be processed immediately.`);
                    setShowUpgradeModal(false);
                    setSelectedUpgradePlan(null);
                    // Refresh page data after a short delay to let webhook process
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                }
            } else {
                message.error(response.data.error || 'Failed to upgrade trial');
            }
        } catch (error) {
            console.error('Error upgrading trial:', error);
            // Check if error is due to non-primary user attempting to upgrade
            if (error.response && error.response.status === 403) {
                message.error('Failed to upgrade, please contact the primary user');
            } else {
                message.error(error.response?.data?.error || 'Failed to upgrade trial');
            }
        }
    };

    const handlePaymentFormSubmit = async (event) => {
        event.preventDefault();
        
        if (!checkout || !checkout.stripe || !checkout.elements) {
            console.error('Stripe not initialized');
            setPaymentStatus('Payment system not ready. Please try again.');
            return;
        }

        setPaymentStatus('Processing...');

        const { error } = await checkout.stripe.confirmSetup({
            elements: checkout.elements,
            confirmParams: {
                return_url: `${window.location.origin}/settings`,
            },
        });

        if (error) {
            console.error('Setup failed:', error);
            setPaymentStatus(`Failed: ${error.message}`);
            message.error(`Failed: ${error.message}`);
        } else {
            // Success - check if this is a trial upgrade
            if (paymentData && paymentData.isTrialUpgrade) {
                setPaymentStatus('Payment method added successfully. Upgrading trial...');
                message.success('Payment method added! Your trial is being upgraded.');
                // Close payment form
                setShowPaymentForm(false);
                setPaymentData(null);
                setCheckout(null);
                // Refresh page after a short delay to let webhook process the upgrade
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                // Regular payment method update
                setPaymentStatus('Payment method updated successfully!');
                message.success('Payment method updated successfully!');
                setShowPaymentForm(false);
                setPaymentData(null);
                setCheckout(null);
                // Refresh to show updated payment method
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            }
        }
    };

    const handleClosePaymentForm = () => {
        setShowPaymentForm(false);
        setPaymentData(null);
        setCheckout(null);
        setPaymentStatus('');
    };

    useEffect(() => {
        // Apply same background as RankingPage
        document.body.style.background = '#121212';
        document.body.style.minHeight = '100vh';

        return () => {
            document.body.style.background = null;
            document.body.style.minHeight = null;
        };
    }, []);

    useEffect(() => {
        // Check if cookie exists
        if(!getCookie("userSession")) {
            console.log("Warning no cookie found:")
            handleLogoutAndRedirect();
            return
        }

        fetchSettings();
        fetchOrgUsers();

        // Check for redirect parameters in the URL
        const url = window.location.href;
        const urlObj = new URL(url);

        // Check for Bullhorn redirect
        const code = urlObj.searchParams.get('code');
        const state = urlObj.searchParams.get('state');

        // Process Bullhorn redirect if present
        if (code && state && state.startsWith('bullhorn-')) {
            console.log("Detected Bullhorn redirect with code and state");
            setProcessingBullhornRedirect(true);
            processBullhornRedirect(code, state);

            // Clean up URL for better user experience
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        // Check for JobAdder redirect
        const stateReceived = urlObj.searchParams.get('state');
        if (stateReceived && !stateReceived.startsWith('bullhorn-')) { // Skip if it's a Bullhorn state
            console.log("State variable returned")
            if (stateReceived.length < 10) {
                console.log("Invalid return URL state")
                message.error("There was an error with the JobAdder Authorisation")
            } else if (stateReceived.substring(stateReceived.length-2) == "-1") {
                console.log("Determined reauth attemp")
                setReturnedReauth(true);
            } else if (stateReceived.substring(stateReceived.length-2) == "-0") {
                console.log("Determined full integratino attempt")
                setReturnedReauth(false);
            } else {
                console.log("Invalid return URL state")
                message.error("There was an error with the JobAdder Authorisation")
            }
        }

        const codeParam = urlObj.searchParams.get('code');
        // Only process for JobAdder if state doesn't start with 'bullhorn-'
        if (codeParam && (!stateReceived || !stateReceived.startsWith('bullhorn-'))) {
            console.log("Non null code Param set:", codeParam)
            setJobAdderAuthCode(codeParam);
        }

        const authError = urlObj.searchParams.get('error');
        if (authError) {
            message.error("Authorisation for Job Adder Integration was denied", 8);
        }
    }, []);

    // Handle Bullhorn redirect processing
    // Handle Bullhorn redirect processing
    const processBullhornRedirect = async (code, state) => {
        try {
            console.log("Processing Bullhorn redirect with code:", code, "state:", state);

            const requestData = {
                sessionID: getCookie("userSession"),
                code,
                state
            };

            const response = await axios.post(
                `${backend_url}/v1/bullhorn-complete`,
                requestData
            );

            if (response.data.success) {
                message.success("Bullhorn integration completed successfully");
                fetchSettings();
            } else {
                message.error("Failed to complete Bullhorn integration");
            }
        } catch (error) {
            console.error("Error completing Bullhorn integration:", error);
            if (error.response && error.response.data === "NO_CUSTOM_ACTIONS") {
                message.error(
                    "No RecX custom actions found in Bullhorn. Please contact your Bullhorn administrator to configure the RecX integration in Bullhorn before continuing.",
                    10
                );
            } else {
                let errorMessage = "Failed to complete Bullhorn integration";
                if (error.response) {
                    errorMessage += `: ${error.response.status} - ${JSON.stringify(error.response.data)}`;
                } else if (error.request) {
                    errorMessage += ": No response received from server";
                } else {
                    errorMessage += `: ${error.message}`;
                }
                message.error(errorMessage);
            }
        } finally {
            setProcessingBullhornRedirect(false);
        }
    };

    // Handle when there is a job adder auth code in the URL i.e returning from the job adder auth page
    useEffect(() => {
        console.log("Change in jobAdderAuthCode to:", jobAdderAuthCode)
        if (jobAdderAuthCode) {
            integrateJobAdder(jobAdderAuthCode, returnedReauth);
            if (returnedReauth) {
                message.success("Received authorisation from JobAdder. Processing authorisation change...", 8);
            } else {
                message.warning("Received authorisation from JobAdder. Proceeding to set up the integration.", 10);
            }
            setIntegrateLoading(true);
        }
    }, [jobAdderAuthCode]);

    const appendCookieToFormData = async () => {
        var userCookie = "default";

        if(!getCookie("userSession")) {
            console.log("Warning no cookie found:")
            handleLogoutAndRedirect();
        } else {
            userCookie = getCookie("userSession");
            console.log("Already had user cookie with value: ", userCookie);
        }
        return userCookie
    }

    const fetchSettings = async () =>{
        try {
            let userCookie = await appendCookieToFormData()
            console.log("User cookie: ", userCookie);
            const SettingsRequestForm = new FormData();
            SettingsRequestForm.append('session-cookie', userCookie);
            SettingsRequestForm.append('jd-type', "all");
            const response = await axios({
                method: 'post',
                url: backend_url + '/v1/populate-settings',
                data: SettingsRequestForm,
                headers: {'Content-Type': 'multipart/form-data' }
            });
            console.log('Response from server:', response.data);
            console.log('Plan data:', {
                plan_type: response.data.plan_type,
                next_payment_date: response.data.next_payment_date,
                billing_status: response.data.billing_status
            });

            setJobLimits({
                CVUsage: response.data.CVUsage,
                CVLimit: response.data.CVLimit,
                NumOpenJob: response.data.NumOpenJob,
                JobLimit: response.data.JobLimit,
                PrescreenUsage: response.data.prescreen_usage || 0,
                PrescreenLimit: response.data.prescreen_limit || 0,
                plan_type: response.data.plan_type || '',
                next_payment_date: response.data.next_payment_date || '',
                billing_status: response.data.billing_status || ''
            });

            if (response.data.JobList && response.data.JobList.length > 0) {
                setExistingJDList(response.data.JobList.map(job => ({
                    ...job,
                    JobID: job.JobID,
                    status: job.JobStatus,
                    key: job.JobID,
                })));
            } else {
                setExistingJDList([]);
            }

            setJobAdderIntegrated(response.data.JobAdderStatus);
            setjobAdderURL(response.data.JobAdderURL);
            console.log("JobAdderURL returned from main settings call:", response.data.JobAdderURL);
        } catch (error) {
            console.error('Failed to fetch settings:', error);
            message.error('Could not load settings.');
        }
    }

    const goBack = () => {
        navigate('/');
    };

    const goToHistorical = () => {
        // Navigate to the historical scores page
        const step = 3
        navigate(`/?step=${step}`);
    };

    // Fetch plans when reactivate modal opens
    useEffect(() => {
        if (showReactivateModal) {
            fetchReactivationPlans();
        }
    }, [showReactivateModal]);

    const closeJob = async (jobId) => {
        try {
            console.log("Job ID to close: ", jobId);
            const query_url = "?sessionID=" + getCookie("userSession") + "&jobID=" + encodeURIComponent(jobId);
            const response = await axios.put(backend_url + `/v1/close-job${query_url}`);
            if (response.status === 200) {
                const updatedList = existingJDList.map(job =>
                    job.JobID === jobId ? { ...job, status: 'closed' } : job
                );
                setExistingJDList(updatedList);
                setJobLimits(prev => ({ ...prev, NumOpenJob: prev.NumOpenJob - 1 }));
                message.success('Job closed successfully');
            } else {
                throw new Error('Server responded with an error');
            }
        } catch (error) {
            console.error('Error closing job:', error);
            message.error('Failed to close the job');
        }
    };

    // Filter jobs based on hideClosedJobs state
    const getFilteredJobs = () => {
        if (hideClosedJobs) {
            return existingJDList.filter(job => job.status === 'open');
        }
        return existingJDList;
    };

    // Toggle function for hiding/showing closed jobs
    const toggleClosedJobs = () => {
        setHideClosedJobs(!hideClosedJobs);
    };

    const columns = [
        { title: 'Job Title', dataIndex: 'JobName', key: 'JobName', width:'60%' },
        { title: 'Status', dataIndex: 'status', key: 'status', width: '20%' },
        { title: 'Action', key: 'action', width:'20%', render: (_, record) => (
                record.status === 'open' && (
                    <Button onClick={() => closeJob(record.JobID)} type="primary" danger>
                        Close Job
                    </Button>
                )
            )}
    ];

    const integrateJobAdderURL = (reauth) => {
        // Generates the URL to send the user to. This is now only used by the reauth button
        // Used to be the both the reauth and main setup buttons (which have slightly different urls)
        console.log("Integrating JobAdder: getting URL")
        const queryURL = `?sessionID=${getCookie("userSession")}&reauth=${reauth}`;
        axios.get(backend_url + `/v1/jobadder-url${queryURL}`)
            .then(response => {
                console.log('URL Response:', response.data)
                setReauthJobAdderURL(response.data);
            })
            .catch(error => {
                console.error('Error retrieving JobAdder permissions url:', error);
                message.error("Failed to set up JobAdder Integration. Please refresh and try again in a few mins. If the error occurs again please contact RecruitmentX for support", 15)
            });
    };

    const removeJobAdderIntegration = () => {
        console.log("Removing JobAdder Integration process begun")
        const queryURL = "?sessionID=" + getCookie("userSession");
        axios.get(backend_url + `/v1/delete-jobadder${queryURL}`)
            .then(response => {
                console.log('Response:', response);
                setDeleteLoading(false);
                setShowDeleteText(true);
                setShowConfirmationModal(false);
                message.success("JobAdder Integration Removed", 5)
            })
            .catch(error => {console.error('Error during JobAdder removal:', error)
                message.success("Failed to remove JobAdder Integration", 5);
                setShowConfirmationModal(false);
            });
    };

    const integrateJobAdder = (authCode, reauth) => {
        console.log("Main JobAdder integration set up begun")
        setIntegrateLoading(true);
        const queryURL = "?sessionID=" + getCookie("userSession") + "&authcode=" + authCode + "&reauth=" + reauth;
        axios.get(backend_url + `/v1/initiate-jobadder${queryURL}`)
            .then(response => {
                console.log('Response from integrate jobAdder:', response);
                setIntegrateLoading(false);
                setJobAdderIntegrated(true);
                message.success("JobAdder Integration Completed", 10)
                message.warning("Note that it may take 5-10mins for the integration to register in your jobadder dashboard", 10);
            })
            .catch(error => {
                console.error('Error during JobAdder creation:', error);
                setIntegrateLoading(false);
                message.error('Failed to create the JobAdder Integration.', 10);
            });
    };

    const confirmationModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        bgcolor: 'white',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        p: '30px',
        textAlign: 'center',
    };

    // Show loading spinner if processing Bullhorn redirect
    if (processingBullhornRedirect) {
        return (
            <ThemeProvider theme={m3ExpressiveTheme}>
                <CssBaseline />
                <div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: '20px', color: 'white' }}>Completing Bullhorn integration...</p>
                </div>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={m3ExpressiveTheme}>
            <CssBaseline />
            <div style={{ width: '100%', margin: '0px auto', maxWidth: '3000px', padding: '0 0px', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'transparent'}}>
                <NavigationBar
                    loggedIn={loggedIn}
                    onLogout={handleLogoutAndRedirect}
                    goToHistorical={goToHistorical}
                    goToFirstStep={goBack}
                    goToSettings={() => navigate('/settings')}
                />

                <div style={{
                    marginLeft: '5%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '1045px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '24px',
                    padding: '32px',
                    marginTop: '20px',
                    marginBottom: '40px'
                }}>
                    {/* Custom styles for accessibility */}
                    <style jsx>{`
                        .ant-collapse-header {
                            color: #ffffff !important;
                        }
                        .ant-collapse-content-box {
                            color: #ffffff !important;
                        }
                        .ant-collapse > .ant-collapse-item > .ant-collapse-header {
                            color: #ffffff !important;
                        }
                        .ant-table-thead > tr > th {
                            background-color: rgba(255, 255, 255, 0.1) !important;
                            color: #ffffff !important;
                            border-color: rgba(255, 255, 255, 0.2) !important;
                        }
                        .ant-table-tbody > tr > td {
                            color: #ffffff !important;
                            border-color: rgba(255, 255, 255, 0.1) !important;
                        }
                        .ant-table-tbody > tr:hover > td {
                            background-color: rgba(255, 255, 255, 0.1) !important;
                            color: #ffffff !important;
                        }
                        .ant-table-tbody > tr:hover {
                            background-color: rgba(255, 255, 255, 0.1) !important;
                        }
                        .ant-table {
                            background: transparent !important;
                        }
                        .ant-table-container {
                            background: transparent !important;
                        }
                    `}</style>

                    <div style={{width: '100%'}}>
                        <h1 style={{ color: '#ffffff', marginBottom: '24px' }}>Settings</h1>
                        <Collapse
                            bordered={false}
                            defaultActiveKey={[]}
                            style={{
                                width: '100%',
                                background: 'transparent'
                            }}
                            className="settings-collapse"
                        >
                            <Panel
                                header={<span style={{ color: '#ffffff', fontSize: '16px', fontWeight: '500' }}>Plan Status</span>}
                                key="plan-status"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '8px',
                                    marginBottom: '16px'
                                }}
                            >
               <div style={{ padding: '16px 0' }}>
                   <Descriptions 
                       bordered 
                       column={1} 
                       size="small"
                       labelStyle={{ color: '#ffffff', fontWeight: '500' }}
                       contentStyle={{ color: '#ffffff' }}
                   >
                       <Descriptions.Item label="Current Plan">
                           {jobLimits.plan_type || 'N/A'}
                       </Descriptions.Item>
                       <Descriptions.Item label="Next Payment Date">
                           {jobLimits.next_payment_date || 'N/A'}
                       </Descriptions.Item>
                       <Descriptions.Item label="Billing Status">
                           <Tag color={jobLimits.billing_status === 'active' ? 'green' : jobLimits.billing_status === 'trial' ? 'blue' : jobLimits.billing_status === 'expired' ? 'red' : 'default'}>
                               {jobLimits.billing_status || 'Unknown'}
                           </Tag>
                       </Descriptions.Item>
                       <Descriptions.Item label="CVs Processed this Period">
                           {`${jobLimits.CVUsage}/${jobLimits.CVLimit}`}
                       </Descriptions.Item>
                       <Descriptions.Item label="Open Jobs">
                           {`${jobLimits.NumOpenJob}/${jobLimits.JobLimit}`}
                       </Descriptions.Item>
                       <Descriptions.Item label="Prescreen request this Period">
                           {`${jobLimits.PrescreenUsage}/${jobLimits.PrescreenLimit}`}
                       </Descriptions.Item>
                   </Descriptions>
                                    <div style={{ marginTop: '16px', textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <Button 
                                            type="primary" 
                                            size="small"
                                            onClick={() => handleSubscriptionAction('manage')}
                                        >
                                            Manage Subscription
                                        </Button>
                                        {(jobLimits.billing_status === 'canceled' || 
                                          jobLimits.billing_status === 'expired' || 
                                          jobLimits.billing_status === 'suspended') && (
                                            <Button 
                                                type="primary" 
                                                size="small"
                                                style={{ background: '#22c55e', borderColor: '#22c55e' }}
                                                onClick={() => setShowReactivateModal(true)}
                                            >
                                                Reactivate Service
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Panel>
                            <Panel
                                header={<span style={{ color: '#ffffff', fontSize: '16px', fontWeight: '500' }}>Manage Jobs</span>}
                                key="1"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    marginBottom: '8px'
                                }}
                            >
                                <div style={{ color: '#ffffff' }}>
                                    {/* Add the toggle button above the table */}
                                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Switch
                                            checked={hideClosedJobs}
                                            onChange={toggleClosedJobs}
                                            size="small"
                                        />
                                        <span style={{ fontSize: '14px', color: '#ffffff' }}>
                                            {hideClosedJobs ? (
                                                <>
                                                    <EyeInvisibleOutlined style={{ marginRight: '4px' }} />
                                                    Closed jobs hidden
                                                </>
                                            ) : (
                                                <>
                                                    <EyeOutlined style={{ marginRight: '4px' }} />
                                                    Show all jobs
                                                </>
                                            )}
                                        </span>
                                        {hideClosedJobs && (
                                            <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', marginLeft: '8px' }}>
                                                ({existingJDList.filter(job => job.status === 'closed').length} closed jobs hidden)
                                            </span>
                                        )}
                                    </div>
                                    <Table
                                        dataSource={getFilteredJobs()}
                                        columns={columns}
                                        rowKey="_id"
                                        style={{
                                            background: 'transparent'
                                        }}
                                    />
                                </div>
                            </Panel>
                        </Collapse>
                    </div>
                    <div style={{ marginTop: '20px', width: '100%'}}>
                        <Collapse
                            bordered={false}
                            defaultActiveKey={[]}
                            style={{
                                width: '100%',
                                background: 'transparent'
                            }}
                        >
                            <Panel
                                header={<span style={{ color: '#ffffff', fontSize: '16px', fontWeight: '500' }}>Integrations</span>}
                                key="2"
                                style={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    marginBottom: '8px'
                                }}
                            >
                                <div style={{ color: '#ffffff' }}>
                                    {/* JobAdder Integration Panel */}
                                    <Collapse
                                        bordered={false}
                                        defaultActiveKey={[]}
                                        style={{
                                            width: '100%',
                                            background: 'transparent'
                                        }}
                                    >
                                        <Panel
                                            header={<span style={{ color: '#ffffff', fontSize: '14px' }}>JobAdder</span>}
                                            key="1"
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.03)',
                                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                                borderRadius: '8px',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            <div style={{ color: '#ffffff' }}>
                                                {!jobAdderIntegrated ? (
                                                    <Button style={{backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px'}} onClick={() => window.location.href = jobAdderURL} loading={integrateLoading}>
                                                        {integrateLoading ? '' : 'Integrate with JobAdder'}
                                                    </Button>
                                                ) :  showDeleteText ? null : (
                                                    <>
                                                        <Button style={{backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px'}} onClick={() => {setShowConfirmationModal(true)}}>
                                                            {deleteLoading ? '' : 'Remove Integration with JobAdder'}
                                                        </Button>
                                                        <Button style={{backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', marginLeft: '15px'}} onClick={() => integrateJobAdderURL(true)} loading={deleteLoading}>
                                                            {deleteLoading ? '' : 'Reauthorise JobAdder Integration'}
                                                        </Button>
                                                    </>
                                                )}

                                                {reauthJobAdderURL !== "" &&
                                                    <div style={{ color: '#ffffff', marginTop: '16px' }}>
                                                        <p>Please go to the following website: <a href={reauthJobAdderURL} target="_self" rel="noopener noreferrer" style={{ color: '#FA370A', textDecoration: 'underline' }}>link</a></p>
                                                        <p>Note: a JobAdder account with admin privileges is required to authorise the integration </p>
                                                    </div>
                                                }
                                                {showDeleteText &&
                                                    <div style={{ color: '#ffffff', marginTop: '16px' }}>
                                                        <p>Your JobAdder Integration has been deleted. </p>
                                                        <p>Please allow up to 5 minutes for the action button to disappear from your JobAdder dashboards.</p>
                                                        <p>If you wish to re-establish the integration, please wait 5 minutes, refresh this page and go through the integration process again.</p>
                                                    </div>
                                                }
                                            </div>
                                        </Panel>

                                        {/* Bullhorn Integration Panel */}
                                        <Panel
                                            header={<span style={{ color: '#ffffff', fontSize: '14px' }}>Bullhorn</span>}
                                            key="2"
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.03)',
                                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                                borderRadius: '8px',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            <div style={{ color: '#ffffff' }}>
                                                <BullhornIntegrationPanel
                                                    getCookie={getCookie}
                                                    fetchSettings={fetchSettings}
                                                />
                                            </div>
                                        </Panel>
                                    </Collapse>
                                </div>
                            </Panel>
                        </Collapse>

                        {isPrimaryUser && (
                            <div style={{ marginTop: '20px', width: '100%'}}>
                                <Collapse bordered={false} style={{ background: 'transparent' }}>
                                    <Panel
                                        header={<span style={{ color: '#ffffff', fontSize: '16px', fontWeight: '500' }}>Manage Users</span>}
                                        key="users"
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '12px',
                                            marginBottom: '8px'
                                        }}
                                    >
                                        <Button 
                                            onClick={() => setShowAddUserModal(true)}
                                            style={{ marginBottom: '16px', backgroundColor: '#22c55e', color: 'white', border: 'none' }}
                                        >
                                            Add User
                                        </Button>
                                        <Table
                                            dataSource={orgUsers}
                                            rowKey="customerID"
                                            pagination={false}
                                            columns={[
                                                {
                                                    title: <span style={{ color: '#fff' }}>Name</span>,
                                                    render: (r) => <span style={{ color: '#fff' }}>{r.firstName} {r.lastName}</span>
                                                },
                                                {
                                                    title: <span style={{ color: '#fff' }}>Email</span>,
                                                    dataIndex: 'email',
                                                    render: (t) => <span style={{ color: '#fff' }}>{t}</span>
                                                },
                                                {
                                                    title: <span style={{ color: '#fff' }}>Phone</span>,
                                                    dataIndex: 'phone',
                                                    render: (t) => <span style={{ color: '#fff' }}>{t}</span>
                                                },
                                                {
                                                    title: <span style={{ color: '#fff' }}>Role</span>,
                                                    dataIndex: 'isPrimaryUser',
                                                    render: (p) => <Tag color={p ? 'blue' : 'default'}>{p ? 'Primary' : 'Member'}</Tag>
                                                },
                                                {
                                                    title: <span style={{ color: '#fff' }}>Action</span>,
                                                    render: (r) => !r.isPrimaryUser && (
                                                        <Button 
                                                            size="small"
                                                            danger
                                                            onClick={() => handleRemoveUser(r.customerID, `${r.firstName} ${r.lastName}`)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )
                                                }
                                            ]}
                                        />
                                        <div style={{ marginTop: '16px', color: 'rgba(255,255,255,0.7)' }}>
                                            Total: {orgUsers.length}
                                        </div>
                                    </Panel>
                                </Collapse>
                            </div>
                        )}

                        <Modal
                            open={showConfirmationModal}
                            onClose={() => setShowConfirmationModal(false)}
                            aria-labelledby="modal-title"
                            BackdropProps={{
                                style: {
                                    backdropFilter: 'blur(3px)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                },
                            }}
                        >
                            <Box sx={confirmationModalStyle}>
                                <Typography variant="h6" component="h2" style={{ color: '#000000' }}>
                                    Are you sure you want to disconnect the integration?
                                </Typography>
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    mt={2}
                                >
                                    <Button style={{backgroundColor: '#ef4444', color: 'white', marginRight: '10px', border: 'none', borderRadius: '8px'}} onClick={removeJobAdderIntegration} loading={deleteLoading} disabled={deleteLoading}>
                                        {deleteLoading ? 'Disconnecting' : 'Disconnect'}
                                    </Button>
                                    <Button style={{backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '8px'}} onClick={() => {setShowConfirmationModal(false)}}>
                                        {'Cancel'}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                    </div>
                    
                    {/* Subscription Management Modal */}
                    <div>
                        <Modal
                            open={showSubscriptionModal}
                            onCancel={handleSubscriptionModalClose}
                            footer={null}
                            maskClosable={true}
                            width={500}
                            bodyStyle={{
                                padding: 0,
                                maxHeight: '90vh',
                                overflowY: 'auto',
                            }}
                            styles={{
                                content: {
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    boxShadow: 'none',
                                    padding: 0,
                                },
                                header: {
                                    backgroundColor: 'transparent',
                                    borderBottom: 'none',
                                },
                            }}
                            closeIcon={
                                <span style={{ color: '#fff', fontSize: '18px' }}>×</span>
                            }
                        >
                            <Box sx={{
                                bgcolor: '#1a1a1a',
                                borderRadius: '12px',
                                p: 4,
                                color: 'white'
                            }}>
                                <Typography id="subscription-modal-title" variant="h6" component="h2" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
                                    Manage Subscription
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Button
                                        type="default"
                                        size="large"
                                        onClick={() => handleSubscriptionAction('upgrade')}
                                        style={{
                                            backgroundColor: '#555',
                                            borderColor: '#555',
                                            color: 'white',
                                            height: '48px',
                                            fontSize: '16px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Upgrade into Formal Plan
                                    </Button>
                                    
                                    {false && (
                                        <Button
                                            type="default"
                                            size="large"
                                            onClick={() => handleSubscriptionAction('downgrade')}
                                            style={{
                                                backgroundColor: '#555',
                                                borderColor: '#555',
                                                color: 'white',
                                                height: '48px',
                                                fontSize: '16px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            Downgrade Plan
                                        </Button>
                                    )}
                                    
                                    <div>
                                        <Button
                                            type="default"
                                            size="large"
                                            onClick={() => handleSubscriptionAction('update_payment')}
                                            style={{
                                                backgroundColor: '#555',
                                                borderColor: '#555',
                                                color: 'white',
                                                height: '48px',
                                                fontSize: '16px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            Update Payment Method
                                        </Button>
                                        {(jobLimits.billing_status === 'trial' || jobLimits.plan_type === 'freetrail_7day') && (
                                            <p style={{
                                                fontSize: '12px',
                                                color: '#a0a0a0',
                                                marginTop: '4px',
                                                marginBottom: 0,
                                                textAlign: 'center'
                                            }}>
                                                Updating will charge your card immediately
                                            </p>
                                        )}
                                    </div>
                                    
                                    <Button
                                        type="default"
                                        size="large"
                                        onClick={() => handleSubscriptionAction('cancel')}
                                        style={{
                                            backgroundColor: '#555',
                                            borderColor: '#555',
                                            color: 'white',
                                            height: '48px',
                                            fontSize: '16px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Cancel Subscription
                                    </Button>
                                    
                                    <Button
                                        type="default"
                                        size="large"
                                        onClick={handleSubscriptionModalClose}
                                        style={{
                                            backgroundColor: '#666',
                                            borderColor: '#666',
                                            color: 'white',
                                            height: '48px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            marginTop: '16px'
                                        }}
                                    >
                                        Close
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>

                        <Modal
                            open={showPaymentForm}
                            onCancel={handleClosePaymentForm}
                            footer={null}
                            closable={false}
                            maskClosable={true}
                            width="auto"
                            styles={{
                                body: { padding: 0 },
                                content: { 
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    padding: 0
                                }
                            }}
                        >
                            <div style={{
                                width: '90%',
                                maxWidth: '500px',
                                maxHeight: '90vh',
                                overflow: 'auto',
                                background: 'linear-gradient(135deg, #141414 0%, #1f1520 50%, #141414 100%)',
                                borderRadius: '24px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                padding: '40px',
                                margin: '0 auto',
                                position: 'relative'
                            }}>
                                {/* Close button */}
                                <button
                                    onClick={handleClosePaymentForm}
                                    style={{
                                        position: 'absolute',
                                        top: '20px',
                                        right: '20px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '32px',
                                        height: '32px',
                                        cursor: 'pointer',
                                        color: '#fff',
                                        fontSize: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.3s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                >
                                    ×
                                </button>
                                
                                <h2 style={{
                                    fontSize: '28px',
                                    fontWeight: '700',
                                    color: '#FA370A',
                                    textAlign: 'center',
                                    marginBottom: '24px'
                                }}>
                                    Update Payment Method
                                </h2>
                                
                                <p style={{
                                    fontSize: '15px',
                                    color: '#a0a0a0',
                                    textAlign: 'center',
                                    marginBottom: '32px',
                                    lineHeight: '1.6'
                                }}>
                                    Enter your new card information below. This will not charge your card.
                                </p>
                                
                                <form onSubmit={handlePaymentFormSubmit}>
                                    <div 
                                        id="payment-element-update" 
                                        style={{ 
                                            marginBottom: '24px',
                                            padding: '16px',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }}
                                    />
                                    
                                    {paymentStatus && (
                                        <div style={{
                                            marginBottom: '24px',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            background: paymentStatus.includes('Failed') 
                                                ? 'rgba(255, 77, 79, 0.1)' 
                                                : 'rgba(82, 196, 26, 0.1)',
                                            border: `1px solid ${paymentStatus.includes('Failed') 
                                                ? 'rgba(255, 77, 79, 0.2)' 
                                                : 'rgba(82, 196, 26, 0.2)'}`,
                                            color: paymentStatus.includes('Failed') ? '#ff4d4f' : '#52c41a',
                                            fontSize: '14px',
                                            textAlign: 'center'
                                        }}>
                                            {paymentStatus}
                                        </div>
                                    )}
                                    
                                    <div style={{
                                        display: 'flex',
                                        gap: '16px',
                                        justifyContent: 'center'
                                    }}>
                                        <button
                                            type="button"
                                            onClick={handleClosePaymentForm}
                                            style={{
                                                padding: '14px 32px',
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                borderRadius: '12px',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                color: '#ffffff',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={paymentStatus === 'Processing...'}
                                            style={{
                                                padding: '14px 32px',
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                borderRadius: '12px',
                                                border: 'none',
                                                background: paymentStatus === 'Processing...'
                                                    ? 'rgba(255, 255, 255, 0.2)'
                                                    : 'linear-gradient(135deg, #FA370A 0%, #FF6B47 100%)',
                                                color: '#ffffff',
                                                cursor: paymentStatus === 'Processing...' ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.3s ease',
                                                boxShadow: paymentStatus === 'Processing...' 
                                                    ? 'none' 
                                                    : '0 8px 16px rgba(250, 55, 10, 0.3)',
                                                opacity: paymentStatus === 'Processing...' ? 0.6 : 1
                                            }}
                                            onMouseEnter={(e) => {
                                                if (paymentStatus !== 'Processing...') {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(250, 55, 10, 0.4)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (paymentStatus !== 'Processing...') {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(250, 55, 10, 0.3)';
                                                }
                                            }}
                                        >
                                            {paymentStatus === 'Processing...' ? 'Processing...' : 'Update Card'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </Modal>

                        {/* Cancellation Fee Confirmation Modal */}
                        <Modal
                            open={showCancellationFeeModal}
                            onCancel={() => setShowCancellationFeeModal(false)}
                            footer={null}
                            closable={false}
                            maskClosable={true}
                            width="auto"
                            styles={{
                                body: { padding: 0 },
                                content: { 
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    padding: 0
                                }
                            }}
                        >
                            <div style={{
                                width: '90%',
                                maxWidth: '500px',
                                background: 'linear-gradient(135deg, #141414 0%, #1f1520 50%, #141414 100%)',
                                borderRadius: '24px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                padding: '40px',
                                margin: '0 auto',
                                position: 'relative'
                            }}>
                                {/* Close button */}
                                <button
                                    onClick={() => setShowCancellationFeeModal(false)}
                                    style={{
                                        position: 'absolute',
                                        top: '20px',
                                        right: '20px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '32px',
                                        height: '32px',
                                        cursor: 'pointer',
                                        color: '#fff',
                                        fontSize: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.3s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                >
                                    ×
                                </button>
                                
                                <h2 style={{
                                    fontSize: '28px',
                                    fontWeight: '700',
                                    color: '#ffffff',
                                    textAlign: 'center',
                                    marginBottom: '24px'
                                }}>
                                    Cancellation Fee Required
                                </h2>
                                
                                <p style={{
                                    fontSize: '15px',
                                    color: '#a0a0a0',
                                    textAlign: 'center',
                                    marginBottom: '32px',
                                    lineHeight: '1.6'
                                }}>
                                    Your subscription requires a minimum 6-month billing period. 
                                    To cancel now, you need to pay the remaining balance.
                                </p>
                                
                                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                    <div style={{
                                        fontSize: '48px',
                                        fontWeight: '800',
                                        color: '#ef4444',
                                        marginBottom: '8px'
                                    }}>
                                        A${(cancellationFeeData?.amount / 100).toFixed(2)}
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: 'rgba(255, 255, 255, 0.7)'
                                    }}>
                                        Cancellation Fee
                                    </div>
                                </div>
                                
                                <div style={{
                                    display: 'flex',
                                    gap: '16px',
                                    justifyContent: 'center'
                                }}>
                                    <button
                                        onClick={() => setShowCancellationFeeModal(false)}
                                        style={{
                                            padding: '14px 32px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            color: '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                        }}
                                    >
                                        Don't Cancel
                                    </button>
                                    <button
                                        onClick={handleCancellationFeeConfirm}
                                        style={{
                                            padding: '14px 32px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            borderRadius: '12px',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, #ef4444 0%, #ff6b6b 100%)',
                                            color: '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(239, 68, 68, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.3)';
                                        }}
                                    >
                                        Pay & Cancel
                                    </button>
                                </div>
                            </div>
                        </Modal>

                        {/* Cancellation Fee Payment Modal */}
                        <Modal
                            open={showCancellationPaymentForm}
                            onCancel={() => setShowCancellationPaymentForm(false)}
                            footer={null}
                            closable={false}
                            maskClosable={true}
                            width="auto"
                            styles={{
                                body: { padding: 0 },
                                content: { 
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    padding: 0
                                }
                            }}
                        >
                            <div style={{
                                width: '90%',
                                maxWidth: '500px',
                                maxHeight: '90vh',
                                overflow: 'auto',
                                background: 'linear-gradient(135deg, #141414 0%, #1f1520 50%, #141414 100%)',
                                borderRadius: '24px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                padding: '40px',
                                margin: '0 auto',
                                position: 'relative'
                            }}>
                                {/* Close button */}
                                <button
                                    onClick={() => setShowCancellationPaymentForm(false)}
                                    style={{
                                        position: 'absolute',
                                        top: '20px',
                                        right: '20px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '32px',
                                        height: '32px',
                                        cursor: 'pointer',
                                        color: '#fff',
                                        fontSize: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.3s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                >
                                    ×
                                </button>
                                
                                <h2 style={{
                                    fontSize: '28px',
                                    fontWeight: '700',
                                    color: '#ffffff',
                                    textAlign: 'center',
                                    marginBottom: '24px'
                                }}>
                                    Pay Cancellation Fee
                                </h2>
                                
                                <p style={{
                                    fontSize: '15px',
                                    color: '#a0a0a0',
                                    textAlign: 'center',
                                    marginBottom: '32px',
                                    lineHeight: '1.6'
                                }}>
                                    Complete your payment to cancel the subscription.
                                </p>
                                
                                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                                    <div style={{
                                        fontSize: '48px',
                                        fontWeight: '800',
                                        color: '#ef4444',
                                        marginBottom: '8px'
                                    }}>
                                        A${(cancellationFeeData?.amount / 100).toFixed(2)}
                                    </div>
                                </div>
                                
                                {cancellationPaymentData && (
                                    <div style={{ marginBottom: '32px' }}>
                                        <div id="cancellation-payment-element" style={{
                                            padding: '16px',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }}>
                                            {/* Stripe Payment Element will be mounted here */}
                                        </div>
                                    </div>
                                )}
                                
                                <div style={{
                                    display: 'flex',
                                    gap: '16px',
                                    justifyContent: 'center'
                                }}>
                                    <button
                                        onClick={() => setShowCancellationPaymentForm(false)}
                                        style={{
                                            padding: '14px 32px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            color: '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                        }}
                                    >
                                        Don't Cancel
                                    </button>
                                    <button
                                        onClick={(event) => {
                                            if (window.cancellationPaymentSubmit) {
                                                window.cancellationPaymentSubmit(event);
                                            }
                                        }}
                                        style={{
                                            padding: '14px 32px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            borderRadius: '12px',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, #ef4444 0%, #ff6b6b 100%)',
                                            color: '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(239, 68, 68, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.3)';
                                        }}
                                    >
                                        Pay Now
                                    </button>
                                </div>
                            </div>
                        </Modal>
                    </div>
                    
                    <div>
                        <Button
                            className="hoverEffect"
                            type="default"
                            onClick={() => navigate('/')}
                            style={{
                                marginTop: '55px',
                                color:'white',
                                backgroundColor:'#FA370A',
                                borderRadius: '24px',
                                padding: '16px 32px',
                                border: 'none',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <LeftOutlined /> Go Back
                        </Button>
                    </div>
                </div>
            </div>

            {/* Reactivate Subscription Modal */}
            <Modal
                open={showReactivateModal}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        setShowReactivateModal(false);
                        setSelectedReactivatePlan(null);
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                slotProps={{
                    backdrop: {
                        onClick: () => {
                            setShowReactivateModal(false);
                            setSelectedReactivatePlan(null);
                        }
                    }
                }}
            >
                <Box sx={{
                    width: '90%',
                    maxWidth: 800,
                    maxHeight: '90vh',
                    overflow: 'auto',
                    bgcolor: 'rgba(10, 10, 15, 0.98)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    p: 4,
                    color: 'white'
                }}>
                    <Typography variant="h5" component="h2" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                        Reactivate Your Service
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                        Select a plan to reactivate your service. Payment will be processed immediately.
                    </Typography>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '16px',
                        marginBottom: '24px'
                    }}>
                        {reactivatePlans.map((plan) => (
                            <div
                                key={plan.id}
                                onClick={() => setSelectedReactivatePlan(plan)}
                                style={{
                                    padding: '20px',
                                    background: selectedReactivatePlan?.id === plan.id 
                                        ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1))' 
                                        : 'rgba(255, 255, 255, 0.05)',
                                    border: selectedReactivatePlan?.id === plan.id 
                                        ? '2px solid #22c55e' 
                                        : '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                                            {plan.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '4px' }}>
                                            {plan.yearly_cv_limit} CVs/year
                                        </Typography>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <Typography variant="h5" sx={{ color: '#22c55e', fontWeight: 'bold' }}>
                                            A${(plan.price / 100).toFixed(2)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            {plan.billing_type === 'recurring' ? '/month' : 'one-time'}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            onClick={() => {
                                setShowReactivateModal(false);
                                setSelectedReactivatePlan(null);
                            }}
                            sx={{
                                backgroundColor: '#6b7280',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#4b5563',
                                },
                                borderRadius: '8px',
                                padding: '10px 24px',
                                textTransform: 'none',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleReactivateSubscription}
                            disabled={!selectedReactivatePlan}
                            sx={{
                                backgroundColor: '#22c55e',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#16a34a',
                                },
                                '&:disabled': {
                                    backgroundColor: '#374151',
                                    color: '#6b7280',
                                },
                                borderRadius: '8px',
                                padding: '10px 24px',
                                textTransform: 'none',
                            }}
                        >
                            Reactivate Service
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Upgrade Modal - Trial or Non-Trial */}
            <Modal
                open={showUpgradeModal}
                onCancel={() => {
                    setShowUpgradeModal(false);
                    setSelectedUpgradePlan(null);
                }}
                footer={null}
                closable={false}
                maskClosable={true}
                width="auto"
                styles={{
                    body: { padding: 0 },
                    content: { 
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        padding: 0
                    }
                }}
            >
                {isUpgradeModalForTrial ? (
                    // Trial Upgrade Modal with improved design
                    <div style={{
                        width: '100%',
                        maxWidth: '1400px',
                        maxHeight: '95vh',
                        overflow: 'auto',
                        background: 'linear-gradient(135deg, #141414 0%, #1f1520 50%, #141414 100%)',
                        borderRadius: '24px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '40px',
                        position: 'relative',
                        margin: '0 auto',
                    }}>
                        <style>{`
                            @keyframes spin {
                                to { transform: rotate(360deg); }
                            }
                        `}</style>
                        {/* Close button */}
                        <button
                            onClick={() => {
                                setShowUpgradeModal(false);
                                setSelectedUpgradePlan(null);
                            }}
                            style={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                color: '#fff',
                                fontSize: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            }}
                        >
                            ×
                        </button>

                        {/* Header */}
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <h2 style={{
                                fontSize: '32px',
                                fontWeight: '700',
                                background: 'linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                marginBottom: '12px',
                                letterSpacing: '-0.02em'
                            }}>
                                Upgrade Your Trial
                            </h2>
                            <p style={{
                                fontSize: '14px',
                                color: '#a0a0a0',
                                marginBottom: '16px'
                            }}>
                                Select a plan to upgrade from your trial. Payment will be processed immediately.
                            </p>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                borderRadius: '24px',
                                fontSize: '13px',
                                color: '#22c55e',
                                fontWeight: '500'
                            }}>
                                <span style={{
                                    width: '16px',
                                    height: '16px',
                                    background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    color: '#ffffff',
                                    fontWeight: 'bold'
                                }}>✓</span>
                                Your trial CV usage will be reset to 0
                            </div>
                        </div>

                        {/* Billing Type Tabs */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '32px',
                            gap: '12px'
                        }}>
                            <button
                                style={{
                                    padding: '12px 32px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    borderRadius: '24px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    background: upgradeActiveBillingTab === 'monthly'
                                        ? 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)'
                                        : 'rgba(255, 255, 255, 0.1)',
                                    color: upgradeActiveBillingTab === 'monthly' ? '#ffffff' : '#a0a0a0',
                                    boxShadow: upgradeActiveBillingTab === 'monthly'
                                        ? '0 8px 16px rgba(255, 0, 102, 0.3)'
                                        : 'none'
                                }}
                                onClick={() => {
                                    setUpgradeActiveBillingTab('monthly');
                                    setSelectedUpgradePlan(null);
                                }}
                                onMouseEnter={(e) => {
                                    if (upgradeActiveBillingTab !== 'monthly') {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (upgradeActiveBillingTab !== 'monthly') {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }
                                }}
                            >
                                Recurring Plans
                            </button>
                            <button
                                style={{
                                    padding: '12px 32px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    borderRadius: '24px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    background: upgradeActiveBillingTab === 'oneoff'
                                        ? 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)'
                                        : 'rgba(255, 255, 255, 0.1)',
                                    color: upgradeActiveBillingTab === 'oneoff' ? '#ffffff' : '#a0a0a0',
                                    boxShadow: upgradeActiveBillingTab === 'oneoff'
                                        ? '0 8px 16px rgba(255, 0, 102, 0.3)'
                                        : 'none'
                                }}
                                onClick={() => {
                                    setUpgradeActiveBillingTab('oneoff');
                                    setSelectedUpgradePlan(null);
                                }}
                                onMouseEnter={(e) => {
                                    if (upgradeActiveBillingTab !== 'oneoff') {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (upgradeActiveBillingTab !== 'oneoff') {
                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }
                                }}
                            >
                                One-off Plans
                            </button>
                        </div>

                        {/* Plans Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                            gap: '32px',
                            marginBottom: '32px'
                        }}>
                            {upgradeActiveBillingTab === 'monthly' ? (
                                // Recurring Plans - sorted by tier
                                (() => {
                                    const tierOrder = ['basic_monthly', 'standard_monthly', 'premium_monthly'];
                                    const monthlyPlans = upgradePlans.monthly || [];
                                    const sortedPlans = [];
                                    
                                    // Sort plans by tier order
                                    tierOrder.forEach(tierId => {
                                        const plan = monthlyPlans.find(p => p.id === tierId);
                                        if (plan) sortedPlans.push(plan);
                                    });
                                    
                                    return sortedPlans.map((plan) => {
                                        const isSelected = selectedUpgradePlan?.id === plan.id;
                                        return (
                                            <div
                                                key={plan.id}
                                                onClick={() => setSelectedUpgradePlan(plan)}
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    backdropFilter: 'blur(20px)',
                                                    border: isSelected 
                                                        ? '2px solid #FF0066' 
                                                        : '1px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '24px',
                                                    padding: '40px 32px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    position: 'relative',
                                                    transform: isSelected ? 'translateY(-8px)' : 'translateY(0)',
                                                    boxShadow: isSelected ? '0 12px 24px rgba(255, 0, 102, 0.3)' : 'none',
                                                    textAlign: 'center',
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }
                                                }}
                                            >
                                                <h3 style={{
                                                    fontSize: '28px',
                                                    fontWeight: '700',
                                                    color: '#ffffff',
                                                    marginBottom: '12px',
                                                    letterSpacing: '-0.01em'
                                                }}>
                                                    {plan.name}
                                                </h3>
                                                <div style={{
                                                    fontSize: '48px',
                                                    fontWeight: '800',
                                                    color: '#ffffff',
                                                    marginBottom: '8px',
                                                    letterSpacing: '-0.02em'
                                                }}>
                                                    <span style={{ fontSize: '20px', color: '#a0a0a0', fontWeight: '500' }}>A$</span>
                                                    {(plan.price / 100).toFixed(0)}
                                                </div>
                                                <div style={{ fontSize: '16px', color: '#808080', marginBottom: '32px' }}>
                                                    per {plan.interval}
                                                </div>
                                                <ul style={{ 
                                                    listStyle: 'none', 
                                                    padding: 0, 
                                                    margin: 0, 
                                                    textAlign: 'left',
                                                    flex: 1
                                                }}>
                                                    <li style={{
                                                        fontSize: '16px',
                                                        color: '#e0e0e0',
                                                        marginBottom: '12px',
                                                        paddingLeft: '24px',
                                                        position: 'relative'
                                                    }}>
                                                        <span style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            top: '2px',
                                                            width: '16px',
                                                            height: '16px',
                                                            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '10px',
                                                            color: '#ffffff',
                                                            fontWeight: 'bold'
                                                        }}>✓</span>
                                                        {plan.yearly_cv_limit?.toLocaleString() || 0} CVs per year
                                                    </li>
                                                </ul>
                                            </div>
                                        );
                                    });
                                })()
                            ) : (
                                // One-off Plans with duration selector
                                (() => {
                                    const tiers = ['basic', 'standard', 'premium'];
                                    
                                    const getOneoffPlanForTier = (tier) => {
                                        const tierPlans = (upgradePlans.oneoff || []).filter(p => p.id.includes(tier));
                                        const duration = upgradeSelectedDuration[tier] || '1year';
                                        return tierPlans?.find(p => p.id === `${tier}_${duration}`);
                                    };

                                    const handleDurationChange = (tier, duration) => {
                                        setUpgradeSelectedDuration(prev => ({
                                            ...prev,
                                            [tier]: duration
                                        }));
                                        // Update selected plan if current tier is selected
                                        if (selectedUpgradePlan?.id.includes(tier)) {
                                            const newPlan = getOneoffPlanForTier(tier);
                                            setSelectedUpgradePlan(newPlan);
                                        }
                                    };

                                    return tiers.map((tier) => {
                                        const plan = getOneoffPlanForTier(tier);
                                        if (!plan) return null;
                                        
                                        const isSelected = selectedUpgradePlan?.id === plan.id;
                                        
                                        return (
                                            <div
                                                key={plan.id}
                                                onClick={() => setSelectedUpgradePlan(plan)}
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    backdropFilter: 'blur(20px)',
                                                    border: isSelected 
                                                        ? '2px solid #FF0066' 
                                                        : '1px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '24px',
                                                    padding: '40px 32px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    position: 'relative',
                                                    transform: isSelected ? 'translateY(-8px)' : 'translateY(0)',
                                                    boxShadow: isSelected ? '0 12px 24px rgba(255, 0, 102, 0.3)' : 'none',
                                                    textAlign: 'center'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }
                                                }}
                                            >
                                                <h3 style={{
                                                    fontSize: '28px',
                                                    fontWeight: '700',
                                                    color: '#ffffff',
                                                    marginBottom: '20px',
                                                    letterSpacing: '-0.01em'
                                                }}>
                                                    {plan.name}
                                                </h3>
                                                
                                                {/* Duration Selector */}
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    marginBottom: '20px'
                                                }}
                                                onClick={(e) => e.stopPropagation()}>
                                                    {['1year', '2year', '3year'].map((duration) => (
                                                        <button
                                                            key={duration}
                                                            style={{
                                                                padding: '6px 12px',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                borderRadius: '16px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                background: upgradeSelectedDuration[tier] === duration
                                                                    ? 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)'
                                                                    : 'rgba(255, 255, 255, 0.05)',
                                                                border: upgradeSelectedDuration[tier] === duration
                                                                    ? 'none'
                                                                    : '1px solid rgba(255, 255, 255, 0.1)',
                                                                color: '#e0e0e0'
                                                            }}
                                                            onClick={() => handleDurationChange(tier, duration)}
                                                        >
                                                            {duration.replace('year', 'Y')}
                                                        </button>
                                                    ))}
                                                </div>

                                                <div style={{
                                                    fontSize: '48px',
                                                    fontWeight: '800',
                                                    color: '#ffffff',
                                                    marginBottom: '8px',
                                                    letterSpacing: '-0.02em'
                                                }}>
                                                    <span style={{ fontSize: '20px', color: '#a0a0a0', fontWeight: '500' }}>A$</span>
                                                    {(plan.price / 100).toFixed(0)}
                                                </div>
                                                <div style={{ fontSize: '14px', color: '#808080', marginBottom: '32px' }}>
                                                    for {Math.round(plan.duration / 12)} year{Math.round(plan.duration / 12) > 1 ? 's' : ''}
                                                </div>
                                                
                                                <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
                                                    <li style={{
                                                        fontSize: '16px',
                                                        color: '#e0e0e0',
                                                        paddingLeft: '24px',
                                                        position: 'relative'
                                                    }}>
                                                        <span style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            top: '2px',
                                                            width: '16px',
                                                            height: '16px',
                                                            background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '10px',
                                                            color: '#ffffff',
                                                            fontWeight: 'bold'
                                                        }}>✓</span>
                                                        {plan.yearly_cv_limit?.toLocaleString() || 0} CVs total
                                                    </li>
                                                </ul>
                                            </div>
                                        );
                                    });
                                })()
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: '16px',
                            justifyContent: 'center',
                            paddingTop: '16px'
                        }}>
                            <button
                                onClick={() => {
                                    setShowUpgradeModal(false);
                                    setSelectedUpgradePlan(null);
                                }}
                                style={{
                                    padding: '14px 32px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: '#ffffff',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpgradeFromTrial}
                                disabled={!selectedUpgradePlan}
                                style={{
                                    padding: '14px 32px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: selectedUpgradePlan 
                                        ? 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)'
                                        : 'rgba(255, 255, 255, 0.1)',
                                    color: '#ffffff',
                                    cursor: selectedUpgradePlan ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.3s ease',
                                    boxShadow: selectedUpgradePlan ? '0 8px 16px rgba(255, 0, 102, 0.3)' : 'none',
                                    opacity: selectedUpgradePlan ? 1 : 0.5
                                }}
                                onMouseEnter={(e) => {
                                    if (selectedUpgradePlan) {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 0, 102, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (selectedUpgradePlan) {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 0, 102, 0.3)';
                                    }
                                }}
                            >
                                {selectedUpgradePlan 
                                    ? `Upgrade Now - A$${(selectedUpgradePlan.price / 100).toFixed(2)}`
                                    : 'Select a Plan'
                                }
                            </button>
                        </div>
                    </div>
                ) : (
                    // Feature Under Development Modal
                    <div style={{
                        width: '90%',
                        maxWidth: '500px',
                        background: 'linear-gradient(135deg, #141414 0%, #1f1520 50%, #141414 100%)',
                        borderRadius: '24px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '40px',
                        textAlign: 'center',
                        margin: '0 auto'
                    }}>
                        <h2 style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            color: '#ffffff',
                            marginBottom: '16px'
                        }}>
                            Feature Under Development
                        </h2>
                        
                        <p style={{
                            fontSize: '15px',
                            color: '#a0a0a0',
                            marginBottom: '32px',
                            lineHeight: '1.6'
                        }}>
                            The plan upgrade feature is currently under development. 
                            Please check back soon!
                        </p>

                        <button
                            onClick={() => {
                                setShowUpgradeModal(false);
                            }}
                            style={{
                                padding: '14px 40px',
                                fontSize: '16px',
                                fontWeight: '600',
                                borderRadius: '12px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #FF0066 0%, #FF66CC 100%)',
                                color: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 8px 16px rgba(255, 0, 102, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 0, 102, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(255, 0, 102, 0.3)';
                            }}
                        >
                            Got It
                        </button>
                    </div>
                )}
            </Modal>

            {/* Add User Modal */}
            <Modal
                title="Add New User"
                open={showAddUserModal}
                onOk={handleAddUser}
                onCancel={() => {
                    setShowAddUserModal(false);
                    setNewUserForm({ firstName: '', lastName: '', email: '', phone: '', password: '', isPrimaryUser: false });
                }}
                maskClosable={true}
                okText="Add User"
                styles={{
                    body: { color: '#000' },
                    header: { color: '#000' }
                }}
            >
                <Form layout="vertical">
                    <Form.Item label="First Name" required>
                        <Input value={newUserForm.firstName} onChange={(e) => setNewUserForm({...newUserForm, firstName: e.target.value})} />
                    </Form.Item>
                    <Form.Item label="Last Name" required>
                        <Input value={newUserForm.lastName} onChange={(e) => setNewUserForm({...newUserForm, lastName: e.target.value})} />
                    </Form.Item>
                    <Form.Item label="Email" required>
                        <Input type="email" value={newUserForm.email} onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})} />
                    </Form.Item>
                    <Form.Item label="Phone">
                        <Input value={newUserForm.phone} onChange={(e) => setNewUserForm({...newUserForm, phone: e.target.value})} />
                    </Form.Item>
                    <Form.Item label="Password" required>
                        <Input.Password value={newUserForm.password} onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})} />
                    </Form.Item>
                    <Form.Item label="Set as Primary User">
                        <Switch checked={newUserForm.isPrimaryUser} onChange={(checked) => setNewUserForm({ ...newUserForm, isPrimaryUser: checked })} />
                    </Form.Item>
                </Form>
            </Modal>

        </ThemeProvider>
    );
};

export default SettingsPage;